/**
 * Чекбокс (§2.3 — анатомия контролов, §13 — зоны нажатия).
 *
 * Управляемый: `checked` приходит снаружи, внутреннего состояния нет.
 * Частичное состояние (`indeterminate`) нужно заголовку выделения в таблице —
 * «выделены не все показанные строки», поэтому выставляется на нативном input
 * через ref, атрибутом его задать нельзя.
 *
 * Зона нажатия — вся подпись: 16-пиксельный квадрат сам по себе меньше
 * допустимых 24×24 даже внутри таблицы (§13).
 */
import { forwardRef, useEffect, useRef } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import { IconCheck } from './icons'
import './Checkbox.css'

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'checked' | 'onChange'> {
  checked: boolean
  onChange: (checked: boolean) => void
  indeterminate?: boolean
  label?: ReactNode
  /** Серая строка под подписью: последствие или пояснение (П6). */
  hint?: ReactNode
  className?: string
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { checked, onChange, indeterminate = false, label, hint, disabled = false, className, ...rest },
  ref,
) {
  const innerRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    const input = innerRef.current
    if (input) input.indeterminate = indeterminate
  }, [indeterminate])

  const classes = ['checkbox']
  if (disabled) classes.push('checkbox--disabled')
  if (className) classes.push(className)

  return (
    <label className={classes.join(' ')}>
      <input
        ref={(node) => {
          innerRef.current = node
          if (typeof ref === 'function') ref(node)
          else if (ref) ref.current = node
        }}
        type="checkbox"
        className="checkbox__input"
        checked={checked}
        disabled={disabled}
        aria-checked={indeterminate ? 'mixed' : checked}
        onChange={(event) => onChange(event.currentTarget.checked)}
        {...rest}
      />
      <span className="checkbox__box" aria-hidden="true">
        {indeterminate ? <span className="checkbox__dash" /> : <IconCheck size={16} />}
      </span>
      {label || hint ? (
        <span className="checkbox__text">
          {label ? <span className="t-body">{label}</span> : null}
          {hint ? <span className="checkbox__hint t-meta">{hint}</span> : null}
        </span>
      ) : null}
    </label>
  )
})
